
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { NavItem } from "./navTypes";
import { ChevronDown, ChevronRight } from "lucide-react";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger 
} from "@/components/ui/collapsible";

interface CollapsibleNavItemProps {
  item: NavItem;
  isExpanded: boolean;
  toggleExpanded: (title: string) => void;
}

const CollapsibleNavItem: React.FC<CollapsibleNavItemProps> = ({ 
  item, 
  isExpanded, 
  toggleExpanded 
}) => {
  const location = useLocation();
  const isActive = location.pathname === item.href || 
    item.subItems?.some(subItem => location.pathname === subItem.href);

  return (
    <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(item.title)}>
      <CollapsibleTrigger className="w-full">
        <div
          className={cn(
            "flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors w-full",
            isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground"
          )}
        >
          <div className="flex items-center gap-3">
            <item.icon className="h-5 w-5 shrink-0" />
            <div>
              <div className={cn(
                "text-sm font-medium", 
                isActive ? "text-primary" : ""
              )}>
                {item.title}
              </div>
              <p className="text-xs hidden lg:block text-muted-foreground">
                {item.description}
              </p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-10 space-y-1 pt-1">
        {item.subItems?.map((subItem) => {
          const isSubActive = location.pathname === subItem.href;
          return (
            <Link
              key={subItem.href}
              to={subItem.href}
              className={cn(
                "flex flex-col px-3 py-2 rounded-md hover:bg-accent/50 transition-colors block",
                isSubActive ? "bg-accent/50 text-accent-foreground" : "text-muted-foreground"
              )}
            >
              <div className={cn(
                "text-sm", 
                isSubActive ? "font-medium text-primary" : ""
              )}>
                {subItem.title}
              </div>
              <p className="text-xs hidden lg:block text-muted-foreground">
                {subItem.description}
              </p>
            </Link>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default CollapsibleNavItem;
